import { motion } from "framer-motion";
import { Calculator } from "@/components/landing/Calculator";
import { Zap, ShieldCheck, Leaf, FileSignature, Search, Percent, PartyPopper } from "lucide-react";

const steps = [
  {
    icon: Search,
    number: "01",
    title: "Simule sua economia",
    description: "Coloque o valor da sua conta de luz no simulador e descubra quanto pode economizar todo mês.",
  },
  {
    icon: FileSignature,
    number: "02",
    title: "Assine digitalmente",
    description: "Sem burocracia. Contrato 100% digital, sem sair de casa e sem custos.",
  },
  {
    icon: Percent,
    number: "03",
    title: "Receba os créditos",
    description: "Nossa fazenda solar gera energia e injeta os créditos direto na sua conta de luz.",
  },
  {
    icon: PartyPopper,
    number: "04",
    title: "Pague menos!",
    description: "Você continua com energia normalmente, mas com desconto de 20% a 30% garantido.",
  },
];

export const GDSection = () => {
  return (
    <div id="geracao-distribuida" data-testid="gd-section">
      {/* Section intro */}
      <section className="pt-20 md:pt-28 pb-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row items-center gap-6 md:gap-12"
          >
            <div className="flex-1">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#1a4fa0]/10 text-[#1a4fa0] mb-4">
                <Zap className="w-3 h-3" />
                Energia por Assinatura
              </span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
                Economize na conta de luz <span className="text-[#1a4fa0]">sem instalar nada</span>
              </h2>
              <p className="text-base md:text-lg text-slate-500 leading-relaxed max-w-xl">
                Nossa fazenda solar gera energia limpa e injeta os créditos diretamente na sua conta de luz. 
                Você paga pela nossa energia ao invés da energia das hidrelétricas — e ainda economiza de 20% a 30%.
              </p>

              <div className="flex flex-wrap gap-5 mt-6">
                {[
                  { icon: ShieldCheck, text: "Sem fidelidade" },
                  { icon: Zap, text: "Sem obras" },
                  { icon: Leaf, text: "100% limpa" },
                ].map((badge) => (
                  <div key={badge.text} className="flex items-center gap-2 text-slate-600 text-sm">
                    <badge.icon className="w-4 h-4 text-[#f5b731]" />
                    <span>{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex-1 max-w-md">
              <img
                src="https://images.unsplash.com/photo-1770936994282-8811fb7129ac?auto=format&fit=crop&q=80&w=600"
                alt="Fazenda solar Solisy"
                className="rounded-3xl shadow-xl w-full object-cover"
                style={{ maxHeight: "320px" }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Calculator */}
      <Calculator />

      {/* How it works - GD */}
      <section id="como-funciona-gd" data-testid="how-it-works-gd" className="py-20 md:py-24" style={{ background: "#f8fafc" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#1a4fa0]/10 text-[#1a4fa0] mb-4">
              Passo a Passo
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Como funciona a <span className="text-[#1a4fa0]">assinatura?</span>
            </h2>
            <p className="text-base md:text-lg text-slate-500 max-w-xl mx-auto">
              Economizar na conta de luz nunca foi tão simples. Veja como funciona:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-7 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow duration-300 h-full">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm mb-5" style={{ background: "#1a4fa0" }}>
                    {step.number}
                  </div>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(245, 183, 49, 0.12)" }}>
                    <step.icon className="w-6 h-6" style={{ color: "#e0a31f" }} />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{step.description}</p>
                </div>
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <div className="w-6 h-[2px]" style={{ background: "#1a4fa0", opacity: 0.3 }} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
